import { randomNumGenerator, createCSSClass, isIE } from "../../../core/js/utilities";
import {
    actions,
    createStore,
    dispatchEvent,
    events,
    reducers,
    renderState,
} from "../../../core/js/state-machine";
import { check } from "../../../core/js/helpers/built-in-icons";
import {
    componentName,
    componentClasses,
    defaultProps,
    eventMap,
    itemTypes,
    views,
    schema,
} from "../../../core/components/action-menu/action-menu";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { validate } from "../../../core/js/validate-props";
import { alignments, placements, getPlacement } from "../../../core/js/positioning/placement";

/**
 * Validates ActionMenu required elements
 * @param {HTMLElement} ActionMenu
 * @returns {Boolean}
 */
export const validateHTML = (element, mergedProps) => {
    return (
        !!element.querySelector(mergedProps.trigger) &&
        !!element.querySelector(`.${componentClasses.container.base}`) &&
        !!element.querySelector(`.${componentClasses.menu}`)
    );
};

/**
 * Creates ActionMenu
 * @param {HTMLElement} node
 * @returns ActionMenu
 */
const ActionMenu = (element, props) => {
    const [success, mergedProps] = validateInitialization({
        componentName,
        defaultProps,
        element,
        props,
        schema,
        validateHTML,
        views,
    });
    if (!success) return;

    // Variables
    let itemsList = [];
    let ariaControlsId = null;
    const elementDynamicClassProperties = {};
    let elementDynamicClass = null;
    let elementDynamicClassMemory;

    // States
    let isPressing = false;
    let checkedItems = [];
    let focusedIndex = -1;

    const isDisabled = (element) => {
        return element.getAttribute("aria-disabled") === "true";
    };

    const addCheckedOption = (item) => {
        const optionValue = getCheckedItemValue(item);
        if (!checkedItems.includes(optionValue)) checkedItems.push(optionValue);
    };

    const removeCheckedOption = (item) => {
        const optionValue = getCheckedItemValue(item);
        const optionIndex = checkedItems.indexOf(optionValue);
        if (optionIndex > -1) checkedItems.splice(optionIndex, 1);
    };

    const toggleCheckedItem = (item) => {
        if (isCheckedItem(item)) {
            renderState(views.option.unchecked, item);
            removeCheckedOption(item);
        } else {
            renderState(views.option.checked, item);
            addCheckedOption(item);
        }

        dispatchEvent(eventMap.selectionChange, element, { checkedItems });
    };

    const getCheckedItemValue = (item) => {
        return item.getAttribute("data-value") || item.querySelector("span").textContent;
    };

    const isCheckedItem = (item) => {
        return item.getAttribute("aria-checked") === "true";
    };

    const setCheckedItems = (item) => {
        if (isCheckedItem(item)) checkedItems.push(getCheckedItemValue(item));
    };

    const injectItemIcon = (item) => {
        if (!item.querySelector(`.${componentClasses.check}`)) {
            item.insertAdjacentHTML("beforeend", check({ class: `${componentClasses.check}` }));
        }
    };

    /**
     * Keep all menu items updated inside the itemsList variable with an object that helps with the items usage.
     * This function is used during the initialization and whenever the menu opens to ensure dinamically inserted items will work.
     */
    const updateItems = () => {
        checkedItems = [];

        // Search for <li> so ensure to keep the order of the elements
        itemsList = Array.from(menuElement.querySelectorAll("li")).reduce((result, li) => {
            const isItem = li.classList.contains(componentClasses.item);
            const isLink = li.classList.contains(componentClasses.link);
            const isOption = li.classList.contains(componentClasses.option);

            let focusableElement;
            let itemType;

            if (isItem) {
                focusableElement = li.querySelector("button");
                itemType = itemTypes.item;
            } else if (isLink) {
                focusableElement = li.querySelector("a");
                itemType = itemTypes.link;
            } else if (isOption) {
                focusableElement = li.querySelector("button");
                itemType = itemTypes.option;
                setCheckedItems(focusableElement);
                injectItemIcon(focusableElement);
            }

            if (focusableElement) {
                result.push({
                    element: li,
                    focusableElement,
                    itemType,
                });
            }

            return result;
        }, []);
    };

    const setUserIsPressing = () => {
        isPressing = true;
        renderState(views.wrapper.isPressing, element);
        window.addEventListener("click", unsetUserIsPressing);
    };

    const unsetUserIsPressing = (e) => {
        // Could use e.detail, but using screen readers the first time the button is clicked with enter/space the detail comes 1
        // If a button is used with enter or space keys a window click is triggered
        // Only disable the is pressing class if the user has clicked and not enter/space keys
        if (e.offsetX !== 0 && e.offsetY !== 0) {
            renderState(views.wrapper.default, element);
            window.removeEventListener("click", unsetUserIsPressing);
            isPressing = false;
        }
    };

    const handleTriggerKeydown = (e) => {
        // 13 (enter) and 32 (space) also trigger the isPressing state
        if (!isPressing && [13, 27, 32, 38, 40].includes(e.keyCode)) setUserIsPressing();

        switch (e.keyCode) {
            // Esc
            case 27:
                close();
                triggerElement.focus();
                break;

            // Arrow up
            case 38:
                e.preventDefault();
                if (visibilityStore.getState())
                    setFocusedIndex(getPreviousFocusableIndex(focusedIndex));
                else open();
                break;
            // Arrow down
            // NOTE: Once a screen reader is available arrow down will not open the menu
            // thats expected since arrow down is used to read next line
            // Arrow down and up works on determinated items base on their role, like role="menu|menuitem|etc"
            case 40:
                e.preventDefault();
                if (visibilityStore.getState())
                    setFocusedIndex(getNextFocusableIndex(focusedIndex));
                else open();
                break;
        }
    };

    const handleTriggerMousedown = (e) => {
        // When using screen readers enter/space on a button will trigger a mousedown instead of a keydown
        if (e.detail === 0 && e.offsetX === 0 && e.offsetY === 0) setUserIsPressing();
    };

    const handleContainerKeydown = (e) => {
        if (!isPressing && [27].includes(e.keyCode)) setUserIsPressing();

        switch (e.keyCode) {
            // Esc
            case 27:
                close();
                triggerElement.focus();
                break;
        }
    };

    const handleMenuKeydown = (e) => {
        if (!isPressing && [38, 40].includes(e.keyCode)) setUserIsPressing();

        switch (e.keyCode) {
            // End
            case 35:
                setFocusedIndex(getPreviousFocusableIndex(itemsList.length));
                break;
            // Home
            case 36:
                setFocusedIndex(getNextFocusableIndex(-1));
                break;
            // Arrow up
            case 38:
                e.preventDefault(); // Prevent scrolling
                setFocusedIndex(getPreviousFocusableIndex(focusedIndex));
                break;
            // Arrow down
            case 40:
                e.preventDefault(); // Prevent scrolling
                setFocusedIndex(getNextFocusableIndex(focusedIndex));
                break;
        }
    };

    /**
     * Whenever clicking and item prevent <button> and <a> elements from being clicked when disabled.
     * Note: The combination of aria-disabled, mousedown and click events was used to keep the same strategy in all kind of items
     * since <a> element don't have a disabled state as <button>
     * @param {MouseEvent} e - Html click event
     * @returns {Boolean}
     */
    const handleMenuItemClick = (e) => {
        const itemIndex = itemsList.reduce((acc, item, index) => {
            if (acc === -1 && item.element.contains(e.target)) acc = index;
            return acc;
        }, -1);
        const item = itemsList[itemIndex];

        if (isDisabled(item.focusableElement)) return;
        if (item.itemType === itemTypes.option) toggleCheckedItem(item.focusableElement);
        setFocusedIndex(itemIndex);
    };

    /**
     * Prevent <button> and <a> elements from being naturaly focused when disabled
     * Note: The combination of aria-disabled, mousedown and click events was used to keep the same strategy in all kind of items
     * since <a> element don't have a disabled state as <button>
     * @param {MouseEvent} e - Html mousedown event
     */
    const handleMenuItemMousedown = (e) => {
        const itemIndex = itemsList.reduce((acc, item, index) => {
            if (acc === -1 && item.element.contains(e.target)) acc = index;
            return acc;
        }, -1);
        const item = itemsList[itemIndex];

        e.preventDefault();
        if (isDisabled(item.focusableElement)) e.preventDefault();
    };

    /**
     * Close whenever a scroll different containerElement is triggered and the popup is opened
     */
    const handleCloseOnScroll = (e) => {
        if (visibilityStore.getState() && e.target !== containerElement) close();
    };

    const addItemsEventListeners = () => {
        itemsList.forEach((item) => {
            item.focusableElement.addEventListener("click", handleMenuItemClick);
            item.focusableElement.addEventListener("mousedown", handleMenuItemMousedown);
        });
    };

    const removeItemsEventListeners = () => {
        itemsList.forEach((item) => {
            item.focusableElement.removeEventListener("click", handleMenuItemClick);
            item.focusableElement.removeEventListener("mousedown", handleMenuItemMousedown);
        });
    };

    /**
     * Search for the previous focusable index jumping any disabled item recursively.
     * @param {Number} index - Index of current focused item
     * @returns {Number} - Previous focusable index
     */
    const getPreviousFocusableIndex = (index) => {
        const prevIndex = index - 1;

        if (!itemsList[prevIndex]) {
            return getPreviousFocusableIndex(itemsList.length);
        }
        if (isDisabled(itemsList[prevIndex].focusableElement)) {
            return getPreviousFocusableIndex(prevIndex);
        }
        return prevIndex;
    };

    /**
     * Search for the next focusable index jumping any disabled item recursively.
     * @param {Number} index - Index of current focused item
     * @returns {Number} - Next focusable index
     */
    const getNextFocusableIndex = (index) => {
        const nextIndex = index + 1;

        if (!itemsList[nextIndex]) {
            return getNextFocusableIndex(-1);
        }
        if (isDisabled(itemsList[nextIndex].focusableElement)) {
            return getNextFocusableIndex(nextIndex);
        }
        return nextIndex;
    };

    /**
     * Set the inner variable focusedIndex and focus the item.
     * The scroll of the popper works focusing the item, browsers by default will scroll to keep that focused item on screen.
     * When using groups, the group stay above the first item, so if the first item is focused the component scroll top to be able to see the group.
     * @param {Number} index - Index of the item to be focused
     */
    const setFocusedIndex = (index) => {
        focusedIndex = index;
        itemsList[focusedIndex].focusableElement.focus();
        // Adjust container scrollTop when using groups
        if (focusedIndex === 0) containerElement.scrollTop = 0;
    };

    /**
     * Function triggered whenever the pop element change the visibility.
     * Always keep the items updated, if the user input a new item dinamically to the menu, it will work.
     * @param {Boolean} isVisible - Status if the pop is visible or not
     */
    const onVisibilityChange = (isVisible) => {
        // Always keep items updated on open, this will ensure that the component work with dynamic childrens being added
        if (isVisible) {
            renderState(views.container.positioning, containerElement);
            updateItems();
            addItemsEventListeners();

            const { position } = getPlacement({
                trigger: triggerElement,
                target: containerElement,
                flip: true,
                placement:
                    mergedProps.alignment === alignments.start
                        ? placements.bottomStart
                        : mergedProps.alignment === alignments.end
                        ? placements.bottomEnd
                        : placements.bottomStart,
            });

            updatePosition(position);
            renderState(views.container.visible, containerElement);
        } else {
            renderState(
                {
                    classList: [`!${elementDynamicClassMemory}`],
                    ...views.container.closed,
                },
                containerElement
            );

            renderState(views.container.hidden, containerElement);
            removeItemsEventListeners();
        }
        // NOTE: After the menu is visible aria-expanded="true" would annouce expanded
        // This is not what is happening today, but if we intent to focus the first item after open,
        // it will not be annouced, will only read the new focused item intead of "expanded"
        setFocusedIndex(getNextFocusableIndex(-1));
    };

    /**
     * Generic function to keep props update. In order to work, the props should always be user inside
     * other functions that will ensure that the prop user is always the lastest one.
     * @param {Object} newProps - Object in format of defaultProps
     */
    const update = (newProps) => {
        const [updateSuccess, updateMergedProps] = validate(newProps, schema);
        if (!updateSuccess) return;
        Object.keys(updateMergedProps).forEach((propName) => {
            mergedProps[propName] = updateMergedProps[propName];
        });
    };

    const getCheckedItems = () => {
        return checkedItems;
    };

    const open = () => {
        visibilityStore.dispatch(actions.open());
        dispatchEvent(events.open(componentName), element);
    };

    const close = () => {
        visibilityStore.dispatch(actions.close());
        dispatchEvent(events.close(componentName), element);
    };

    /**
     * Given a position object containing x,y coordinates, it positions the containerElement at that position.
     * @param {object} position
     * @returns {Boolean}
     */
    const updatePosition = (position) => {
        elementDynamicClassProperties.transform = `translate(${parseInt(
            position.left
        )}px, ${parseInt(position.top)}px) !important`;

        elementDynamicClass = `dds__dynamic-action-menu-t${position.top}-l${position.left}`;

        if (createCSSClass(`.${elementDynamicClass}`, elementDynamicClassProperties)) {
            renderState(
                { classList: [elementDynamicClass], ...views.container.opened },
                containerElement
            );
        }

        elementDynamicClassMemory = elementDynamicClass;

        return true;
    };

    // Dispose
    const dispose = () => {
        element[componentName] = null;
        element = null;
        // Remove event listeners
        triggerElement.removeEventListener("click", toggle);
        triggerElement.removeEventListener("keydown", handleTriggerKeydown);
        triggerElement.removeEventListener("mousedown", handleTriggerMousedown);
        triggerElement.removeEventListener("blur", closeOnTargetOutside);
        containerElement.removeEventListener("keydown", handleContainerKeydown);
        containerElement.removeEventListener("blur", closeOnTargetOutside, true);
        menuElement.removeEventListener("keydown", handleMenuKeydown);
        if (mergedProps.closeOnScroll)
            window.removeEventListener("scroll", handleCloseOnScroll, true);
        removeItemsEventListeners();
    };

    const toggle = () => {
        if (visibilityStore.getState()) {
            close();
        } else {
            open();
        }
    };

    const closeOnTargetOutside = (e) => {
        if (isIE && !e.relatedTarget) e.relatedTarget = document.activeElement;

        if (
            visibilityStore.getState() &&
            !triggerElement.contains(e.relatedTarget) &&
            !containerElement.contains(e.relatedTarget)
        )
            close();
    };

    const triggerElement = element.querySelector(mergedProps.trigger);
    const containerElement = element.querySelector(`.${componentClasses.container.base}`);
    const menuElement = element.querySelector(`.${componentClasses.menu}`);

    updateItems();

    ariaControlsId = menuElement.getAttribute("id") || `${componentName}-${randomNumGenerator()}`;
    menuElement.setAttribute("id", ariaControlsId);
    triggerElement.setAttribute("aria-controls", ariaControlsId);

    const visibilityStore = createStore(reducers.boolReducer, false);
    visibilityStore.subscribe(onVisibilityChange);
    // Add event listeners
    triggerElement.addEventListener("click", toggle);
    triggerElement.addEventListener("keydown", handleTriggerKeydown);
    triggerElement.addEventListener("mousedown", handleTriggerMousedown);
    triggerElement.addEventListener("blur", closeOnTargetOutside);
    containerElement.addEventListener("blur", closeOnTargetOutside, true);
    containerElement.addEventListener("keydown", handleContainerKeydown);
    menuElement.addEventListener("keydown", handleMenuKeydown);
    if (mergedProps.closeOnScroll) window.addEventListener("scroll", handleCloseOnScroll, true);

    // Api
    const api = {
        dispose,
        update,
        open,
        close,
        toggle,
        getCheckedItems,
    };

    // Assignment
    element[componentName] = api;

    return api;
};

export default ActionMenu;
